import * as Yup from "yup";
import dayjs from "dayjs";
import {
  ONLY_POSITIVE_NUMBERS,
  PRICE_REGEX,
} from "../../../../../utils/Patterns";
import {
  createTrainingShiftConflictTest,
  getShiftSlotDurationMinutes,
} from "./trainingValidationHelpers";

/* eslint-disable no-invalid-this */

const emptyToNull = (value, originalValue) =>
  originalValue === "" || originalValue === null ? null : value;

const nonNegativeNumber = (message) =>
  Yup.number()
    .transform(emptyToNull)
    .typeError(message)
    .min(0, message)
    .integer(message)
    .nullable();

const requiredNonNegativeNumber = (requiredMessage, numberMessage) =>
  nonNegativeNumber(numberMessage).required(requiredMessage);

const stringifyValue = (v) =>
  v === null || v === undefined || v === "" ? "" : String(v);

/** Admin `RoleFields` margin limit (minutes). */
const MARGIN_LIMIT = 240;

const marginNumeric = (raw) => {
  if (raw === "" || raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

/**
 * Admin portal rules for one pair (check-in or check-out margins).
 * @returns {{ fieldKey: string, message: string } | null}
 */
const marginPairAdminRule = (
  beforeRaw,
  afterRaw,
  messages,
  keys,
) => {
  const { beforeMaxMsg, afterMaxMsg, totalMaxMsg, totalMinMsg } = keys;
  const nB = marginNumeric(beforeRaw);
  const nA = marginNumeric(afterRaw);

  if (nB != null && nB > MARGIN_LIMIT) {
    return { fieldKey: "before", message: beforeMaxMsg };
  }
  if (nA != null && nA > MARGIN_LIMIT) {
    return { fieldKey: "after", message: afterMaxMsg };
  }

  const bothForTotalMax =
    beforeRaw !== "" &&
    beforeRaw != null &&
    afterRaw !== "" &&
    afterRaw != null &&
    nB != null &&
    nA != null &&
    nB > 0 &&
    nA > 0;

  if (bothForTotalMax && nB + nA > MARGIN_LIMIT) {
    return { fieldKey: "after", message: totalMaxMsg };
  }

  if (nB === 0 && nA === 0) {
    return { fieldKey: "before", message: totalMinMsg };
  }

  return null;
};

const fieldKeyToShiftPath = (basePath, pair, fieldKey) => {
  const map =
    pair === "checkIn"
      ? {
          before: `${basePath}.before_check_in_margin`,
          after: `${basePath}.after_check_in_margin`,
        }
      : {
          before: `${basePath}.before_check_out_margin`,
          after: `${basePath}.after_check_out_margin`,
        };
  return map[fieldKey];
};

/** Admin RoleFields: check-in / check-out / overlap vs shift slot length. */
function applyShiftMarginTests(schema, messages) {
  return schema
    .test("shift-margins-check-in", function shiftMarginsCheckIn(shift) {
      if (!shift) return true;
      const hit = marginPairAdminRule(
        shift.before_check_in_margin,
        shift.after_check_in_margin,
        messages,
        {
          beforeMaxMsg: messages.checkinStartMarginMax,
          afterMaxMsg: messages.checkinEndMarginMax,
          totalMaxMsg: messages.checkinMarginsTotalMax,
          totalMinMsg: messages.checkinMarginsTotalMin,
        },
      );
      if (hit) {
        return this.createError({
          path: fieldKeyToShiftPath(this.path, "checkIn", hit.fieldKey),
          message: hit.message,
        });
      }
      return true;
    })
    .test("shift-margins-check-out", function shiftMarginsCheckOut(shift) {
      if (!shift) return true;
      const hit = marginPairAdminRule(
        shift.before_check_out_margin,
        shift.after_check_out_margin,
        messages,
        {
          beforeMaxMsg: messages.checkoutStartMarginMax,
          afterMaxMsg: messages.checkoutEndMarginMax,
          totalMaxMsg: messages.checkoutMarginsTotalMax,
          totalMinMsg: messages.checkoutMarginsTotalMin,
        },
      );
      if (hit) {
        return this.createError({
          path: fieldKeyToShiftPath(this.path, "checkOut", hit.fieldKey),
          message: hit.message,
        });
      }
      return true;
    })
    .test(
      "shift-margins-overlap-slot",
      function shiftMarginsOverlapWithSlot(shift) {
        if (!shift) return true;
        const slot = getShiftSlotDurationMinutes(shift.starts_at, shift.ends_at);
        const nAfterIn = marginNumeric(shift.after_check_in_margin);
        const nBeforeOut = marginNumeric(shift.before_check_out_margin);
        if (slot == null || slot <= 0) return true;
        if (
          shift.after_check_in_margin === "" ||
          shift.after_check_in_margin == null ||
          shift.before_check_out_margin === "" ||
          shift.before_check_out_margin == null ||
          nAfterIn == null ||
          nBeforeOut == null
        ) {
          return true;
        }
        if (nAfterIn + nBeforeOut > slot) {
          return this.createError({
            path: `${this.path}.before_check_out_margin`,
            message: messages.marginsOverlapWithSlot,
          });
        }
        return true;
      },
    );
}

/** Training's own duration in minutes (unlike shifts, start/end are full datetimes). */
const getTrainingDurationMinutes = (trainingStart, trainingEnd) => {
  if (!trainingStart || !trainingEnd) return null;
  const start = dayjs(trainingStart);
  const end = dayjs(trainingEnd);
  if (!start.isValid() || !end.isValid()) return null;
  return end.diff(start, "minute");
};

/** Same check-in / check-out margin caps on paid-training block, plus overlap vs training duration. */
function applyTrainingMarginTests(schema, messages) {
  return schema
    .test("training-margins-check-in", function trainingMarginsCheckIn(t) {
      if (!t) return true;
      const hit = marginPairAdminRule(
        t.before_check_in_margin,
        t.after_check_in_margin,
        messages,
        {
          beforeMaxMsg: messages.checkinStartMarginMax,
          afterMaxMsg: messages.checkinEndMarginMax,
          totalMaxMsg: messages.checkinMarginsTotalMax,
          totalMinMsg: messages.checkinMarginsTotalMin,
        },
      );
      if (hit) {
        return this.createError({
          path: fieldKeyToShiftPath(this.path, "checkIn", hit.fieldKey),
          message: hit.message,
        });
      }
      return true;
    })
    .test("training-margins-check-out", function trainingMarginsCheckOut(t) {
      if (!t) return true;
      const hit = marginPairAdminRule(
        t.before_check_out_margin,
        t.after_check_out_margin,
        messages,
        {
          beforeMaxMsg: messages.checkoutStartMarginMax,
          afterMaxMsg: messages.checkoutEndMarginMax,
          totalMaxMsg: messages.checkoutMarginsTotalMax,
          totalMinMsg: messages.checkoutMarginsTotalMin,
        },
      );
      if (hit) {
        return this.createError({
          path: fieldKeyToShiftPath(this.path, "checkOut", hit.fieldKey),
          message: hit.message,
        });
      }
      return true;
    })
    .test(
      "training-margins-overlap-slot",
      function trainingMarginsOverlapWithSlot(t) {
        if (!t) return true;
        const nAfterIn = marginNumeric(t.after_check_in_margin);
        const nBeforeOut = marginNumeric(t.before_check_out_margin);
        if (
          t.after_check_in_margin === "" ||
          t.after_check_in_margin == null ||
          t.before_check_out_margin === "" ||
          t.before_check_out_margin == null ||
          nAfterIn == null ||
          nBeforeOut == null
        ) {
          return true;
        }
        const duration = getTrainingDurationMinutes(t.starts_at, t.ends_at);
        if (duration == null || duration <= 0) return true;
        if (nAfterIn + nBeforeOut > duration) {
          return this.createError({
            path: `${this.path}.before_check_out_margin`,
            message: messages.marginsOverlapWithSlot,
          });
        }
        return true;
      },
    );
}

/**
 * Paid training: same rules as Admin StepTwo validationSchema (training_attributes + shift conflict).
 * @param {object} messages - resolved strings from inputsValidations
 * @param {string|undefined} eventEndAt - for training start/end vs event end
 */
const trainingAttributesSchema = (messages, eventEndAt) =>
  applyTrainingMarginTests(
    Yup.object().shape({
    name: Yup.string().trim().required(messages.required),

    payroll_amount: Yup.string()
      .transform(stringifyValue)
      .required(messages.required)
      .matches(PRICE_REGEX, messages.trainingPaidAmountInvalid),

    description: Yup.string().trim().required(messages.required),

    number_of_days: Yup.string()
      .transform(stringifyValue)
      .required(messages.required)
      .matches(ONLY_POSITIVE_NUMBERS, messages.positiveInteger),

    starts_at: Yup.string()
      .required(messages.required)
      .test(
        "not-past-datetime",
        messages.pastDateTime,
        function notPastDateTime(value) {
          if (!value) return true;
          const trainingId = this.parent && this.parent.id;
          if (trainingId) return true;
          return dayjs(value).isAfter(dayjs());
        },
      )
      .test(
        "start-not-after-event-end",
        messages.trainingStartMustBeBeforeEventEnd,
        function startBeforeEventEnd(value) {
          if (!value || !eventEndAt) return true;
          return !dayjs(value).isAfter(dayjs(eventEndAt));
        },
      ),

    ends_at: Yup.string()
      .required(messages.required)
      .test(
        "end-after-start",
        messages.trainingEndBeforeStart,
        function endAfterStart(value) {
          const { starts_at } = this.parent;
          if (!value || !starts_at) return true;
          return dayjs(value).isAfter(dayjs(starts_at));
        },
      )
      .test(
        "end-time-not-before-start-time",
        messages.trainingEndTimeBeforeStartTime,
        function endTimeNotBeforeStartTime(value) {
          const { starts_at } = this.parent;
          if (!value || !starts_at) return true;
          if (dayjs(value).isSame(dayjs(starts_at), "day")) return true;
          const startMinutes =
            dayjs(starts_at).hour() * 60 + dayjs(starts_at).minute();
          const endMinutes =
            dayjs(value).hour() * 60 + dayjs(value).minute();
          return endMinutes >= startMinutes;
        },
      )
      .test(
        "end-not-after-event-end",
        messages.trainingEndMustBeBeforeEventEnd,
        function endNotAfterEventEnd(value) {
          if (!value || !eventEndAt) return true;
          return !dayjs(value).isAfter(dayjs(eventEndAt));
        },
      ),

    before_check_in_margin: requiredNonNegativeNumber(
      messages.required,
      messages.nonNegative,
    ),

    after_check_in_margin: requiredNonNegativeNumber(
      messages.required,
      messages.nonNegative,
    ),

    before_check_out_margin: requiredNonNegativeNumber(
      messages.required,
      messages.nonNegative,
    ),

    after_check_out_margin: requiredNonNegativeNumber(
      messages.required,
      messages.nonNegative,
    ),

    location_address: Yup.string()
      .trim()
      .required(messages.required)
      .test(
        "location-has-valid-coords",
        messages.trainingLocationInvalid,
        function locationHasValidCoords(value) {
          if (!value) return true;
          const { lat, lng } = this.parent;
          return !!lat && !!lng;
        },
      ),

    lat: Yup.mixed().nullable(),

    lng: Yup.mixed().nullable(),

    location_radius: Yup.string()
      .transform(stringifyValue)
      .required(messages.required)
      .matches(ONLY_POSITIVE_NUMBERS, messages.positiveInteger),
    }),
    messages,
  );

const trainingSchema = (messages, eventEndAt) =>
  Yup.lazy((value) => {
    if (!value || value._destroy === true) {
      return Yup.mixed().nullable(true);
    }
    return trainingAttributesSchema(messages, eventEndAt);
  });

/** Paid training toggle on but fields incomplete — used for portal draft saves. */
const trainingDraftSchema = Yup.lazy((value) => {
  if (!value || value._destroy === true) {
    return Yup.mixed().nullable(true);
  }
  return Yup.object();
});

const getDefineRolesAndShiftsValidationSchema = (
  messages,
  eventBounds = {},
  opts = {},
) => {
  const { relaxedPaidTraining = false } = opts;
  const { eventStartAt, eventEndAt } = eventBounds;

  const trainingFieldSchema = relaxedPaidTraining
    ? trainingDraftSchema
    : trainingSchema(messages, eventEndAt);

  const shiftRowSchemaInner = applyShiftMarginTests(
    Yup.object().shape({
      shift_type: Yup.string().required(messages.required),
      starts_at: Yup.string().required(messages.required),
      ends_at: Yup.string().required(messages.required),
      payroll_amount: requiredNonNegativeNumber(
        messages.required,
        messages.nonNegative,
      ),
      payroll_per_day: Yup.mixed().nullable(),
      before_check_in_margin: requiredNonNegativeNumber(
        messages.required,
        messages.nonNegative,
      ),
      after_check_in_margin: requiredNonNegativeNumber(
        messages.required,
        messages.nonNegative,
      ),
      before_check_out_margin: requiredNonNegativeNumber(
        messages.required,
        messages.nonNegative,
      ),
      after_check_out_margin: requiredNonNegativeNumber(
        messages.required,
        messages.nonNegative,
      ),
      training_attributes: trainingFieldSchema,
    }),
    messages,
  );

  const shiftRowSchema =
    relaxedPaidTraining
      ? shiftRowSchemaInner
      : shiftRowSchemaInner.test(
          "training-conflict-with-shift",
          messages.trainingConflictWithShift,
          createTrainingShiftConflictTest(
            messages,
            eventStartAt,
            eventEndAt,
          ),
        );

  return Yup.object().shape({
    event_roles_attributes: Yup.array()
      .of(
        Yup.object()
          .shape({
            role_id: Yup.mixed()
              .nullable()
              .test(
                "role-required",
                messages.required,
                function roleSelected(v) {
                  if (v === null || v === undefined) return false;
                  if (typeof v === "string") return v.trim() !== "";
                  if (typeof v === "number") return !Number.isNaN(v);
                  return true;
                },
              ),
            role_name: Yup.string().nullable(),
            // One gender may be 0 when the other isn't (PROLINE-4158); both 0 is refused below.
            maximum_number_of_males: requiredNonNegativeNumber(
              messages.required,
              messages.nonNegative,
            ),
            maximum_number_of_females: requiredNonNegativeNumber(
              messages.required,
              messages.nonNegative,
            ),
            uniform_code: Yup.string()
              .max(200, messages.maxLength)
              .nullable(),
            event_role_shifts_attributes: Yup.array()
              .of(shiftRowSchema)
              .min(1, messages.shiftRequired),
          })
          .test(
            "capacity-not-empty",
            messages.capacityRequired,
            function validateCapacity(role) {
              const males = Number(role?.maximum_number_of_males || 0);
              const females = Number(role?.maximum_number_of_females || 0);

              if (males + females > 0) return true;

              return this.createError({
                path: `${this.path}.maximum_number_of_males`,
                message: messages.capacityRequired,
              });
            },
          ),
      )
      .min(1, messages.roleRequired),
  });
};

export default getDefineRolesAndShiftsValidationSchema;
